import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const HowItWorksContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#0B0813',
  padding: '40px 20px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '30px',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
}));

const ContentCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(11, 8, 19, 0.8)',
  border: '1px solid #00F0FF',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  marginBottom: '20px',
}));

const ContentText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  lineHeight: '1.8',
  marginBottom: '15px',
}));

const HighlightText = styled(Typography)(({ theme }) => ({
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  fontWeight: 'bold',
}));

const StepNumber = styled(Typography)(({ theme }) => ({
  color: '#FFD200',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '16px',
  fontWeight: 'bold',
  marginBottom: '10px',
}));

const HowItWorks: React.FC = () => {
  return (
    <HowItWorksContainer>
      <Container maxWidth="lg">
        <SectionTitle>How It Works</SectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <StepNumber>STEP 1: UPLOAD IMAGE</StepNumber>
                <ContentText>
                  Select an image from your device (PNG, BMP, or JPEG format). The image will serve as the carrier for your hidden message. Larger images can accommodate longer messages.
                </ContentText>
                <ContentText>
                  • Supported formats: PNG, BMP, JPEG<br/>
                  • Maximum file size: 50MB<br/>
                  • Recommended: High-quality images for best results
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <StepNumber>STEP 2: ENCRYPTION</StepNumber>
                <ContentText>
                  Enter your secret message and encryption password. Your message is encrypted using AES-256-GCM before being embedded, ensuring dual-layer security.
                </ContentText>
                <ContentText>
                  • Message: Any text you want to hide<br/>
                  • Password: Minimum 8 characters with mixed case and numbers<br/>
                  • Bit depth: Choose 1, 2, or 4 bits for encoding
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <StepNumber>STEP 3: STEGANOGRAPHY EMBEDDING</StepNumber>
                <ContentText>
                  The encrypted message is embedded into the image using LSB (Least Significant Bit) steganography. This modifies the least significant bits of pixel values to hide data without visible changes.
                </ContentText>
                <ContentText>
                  • 1-bit: Most secure, lowest capacity<br/>
                  • 2-bit: Balanced security and capacity<br/>
                  • 4-bit: Maximum capacity, moderate quality impact
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <StepNumber>STEP 4: SECURE OUTPUT</StepNumber>
                <ContentText>
                  Download the encoded image. It looks identical to the original but contains your hidden message. Share it securely with the recipient, who can decode it using the same password.
                </ContentText>
                <ContentText>
                  • Image appears unchanged to the naked eye<br/>
                  • Contains hidden encrypted message<br/>
                  • Can be shared via any channel<br/>
                  • Recipient needs password to decode
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12}>
            <ContentCard>
              <CardContent>
                <HighlightText>// DECODING PROCESS</HighlightText>
                <ContentText>
                  To retrieve the hidden message:
                </ContentText>
                <ContentText>
                  1. Upload the encoded image<br/>
                  2. Enter the same password used for encryption<br/>
                  3. Select the same bit depth used during encoding<br/>
                  4. Click decode to reveal the hidden message
                </ContentText>
                <ContentText>
                  <HighlightText>// IMPORTANT:</HighlightText> Without the correct password, the message cannot be decrypted, even if the steganography is detected.
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>
        </Grid>
      </Container>
    </HowItWorksContainer>
  );
};

export default HowItWorks;
